import React from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  Chip,
  Dialog,
  Box,
  Typography
} from '@mui/material';
import { buttonStyles, formControlStyles, cardStyles, chipStyles, dialogStyles, layoutStyles } from '../../styles/commonStyles';

// Styled Button Components
export const ActionButton = ({ children, ...props }) => (
  <Button sx={buttonStyles.actionButton} {...props}>
    {children}
  </Button>
);

export const PrimaryButton = ({ children, ...props }) => (
  <Button sx={buttonStyles.primaryButton} {...props}>
    {children}
  </Button>
);

export const SecondaryButton = ({ children, ...props }) => (
  <Button sx={buttonStyles.secondaryButton} {...props}>
    {children}
  </Button>
);

export const SmallButton = ({ children, ...props }) => (
  <Button sx={buttonStyles.smallButton} {...props}>
    {children}
  </Button>
);

export const BackButton = ({ children, ...props }) => (
  <Button sx={buttonStyles.backButton} {...props}>
    {children}
  </Button>
);

// Styled Form Components
export const StyledFormControl = ({ children, size = 'medium', ...props }) => {
  const sizeStyle = size === 'small' ? formControlStyles.small : formControlStyles.standard;
  return (
    <FormControl sx={sizeStyle} {...props}>
      {children}
    </FormControl>
  );
};

export const StyledTextField = ({ size = 'medium', ...props }) => {
  const sizeStyle = size === 'small' ? formControlStyles.small : formControlStyles.standard;
  return (
    <TextField sx={sizeStyle} {...props} />
  );
};

export const StyledSelect = ({ children, ...props }) => (
  <Select {...props}>
    {children}
  </Select>
);

// Styled Card Components
export const ElevatedCard = ({ children, ...props }) => (
  <Card elevation={2} sx={{ mb: 3 }} {...props}>
    {children}
  </Card>
);

export const FilterCard = ({ children, ...props }) => (
  <Card elevation={2} sx={{ mb: 3 }} {...props}>
    {children}
  </Card>
);

export const SummaryCard = ({ children, theme, ...props }) => {
  const summaryStyle = cardStyles.summary(theme);
  return (
    <Card {...summaryStyle} {...props}>
      {children}
    </Card>
  );
};

// Styled Chip Components
export const FilterChip = ({ children, ...props }) => (
  <Chip {...chipStyles.filter} {...props}>
    {children}
  </Chip>
);

export const StatusChip = ({ children, ...props }) => (
  <Chip {...chipStyles.status} {...props}>
    {children}
  </Chip>
);

export const MetadataChip = ({ children, ...props }) => (
  <Chip {...chipStyles.metadata} {...props}>
    {children}
  </Chip>
);

// Styled Dialog Components
export const StyledDialog = ({ children, size = 'standard', ...props }) => {
  const dialogStyle = size === 'large' ? dialogStyles.large : dialogStyles.standard;
  return (
    <Dialog sx={dialogStyle} {...props}>
      {children}
    </Dialog>
  );
};

// Layout Components
export const PageHeader = ({ children, ...props }) => (
  <Box sx={layoutStyles.pageHeader} {...props}>
    {children}
  </Box>
);

export const FilterSection = ({ children, ...props }) => (
  <Box sx={layoutStyles.filterSection} {...props}>
    {children}
  </Box>
);

export const ActionGroup = ({ children, ...props }) => (
  <Box sx={layoutStyles.actionGroup} {...props}>
    {children}
  </Box>
);

// Multi-select filter component with consistent checkbox styling
export const MultiSelectFilter = ({ 
  label, 
  value, 
  onChange, 
  options, 
  renderValue,
  size = 'small',
  minWidth = 120,
  ...props 
}) => (
  <StyledFormControl size={size} sx={{ minWidth }} {...props}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      value={value}
      label={label}
      onChange={onChange}
      renderValue={renderValue || ((selected) => {
        if (selected.length === 0) return '';
        if (selected.length === 1) return selected[0];
        return `${selected.length} selected`;
      })}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                border: '2px solid',
                borderColor: value.includes(option.value) ? 'primary.main' : 'grey.400',
                backgroundColor: value.includes(option.value) ? 'primary.main' : 'transparent',
                borderRadius: '3px',
                mr: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {value.includes(option.value) && (
                <Box sx={{ color: 'white', fontSize: '12px', lineHeight: 1 }}>✓</Box>
              )}
            </Box>
            {option.label}
          </Box>
        </MenuItem>
      ))}
    </Select>
  </StyledFormControl>
);
